import { useState, useEffect } from 'react'
import { Pencil, MapPin, Bookmark } from 'lucide-react'
import { EditProfileModal } from './EditProfileModal'
import { supabase } from '../../lib/supabase'
import { interestsService, Interest } from '../../lib/interests'
import { adminService } from '../../lib/admin'
import { profilesService } from '../../lib/profiles'

interface ProfileHeaderProps {
  user: any
  currentUserId?: string
  onProfileUpdate?: () => void
  viewMode?: 'shared' | 'saved' | 'issues'
  onViewModeChange?: (mode: 'shared' | 'saved' | 'issues') => void
  tabAction?: React.ReactNode
}

export function ProfileHeader({ user, currentUserId, onProfileUpdate, viewMode = 'shared', onViewModeChange, tabAction }: ProfileHeaderProps) {
  const [displayName, setDisplayName] = useState(`${user.user_metadata?.first_name || user.first_name || ''} ${user.user_metadata?.last_name || user.last_name || ''}`.trim() || user.email)
  const [avatarUrl, setAvatarUrl] = useState(user.user_metadata?.avatar_url || user.avatar_url || '')
  const [username, setUsername] = useState(user.username || user.email?.split('@')[0] || '')
  const [bio, setBio] = useState(user.bio || '')
  const [location, setLocation] = useState(user.location || '')
  const [interests, setInterests] = useState<Interest[]>([])
  const [showEditProfileModal, setShowEditProfileModal] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  const [likesCount, setLikesCount] = useState(0)

  // Check if this is the current user's own profile
  const isOwnProfile = currentUserId && user.id === currentUserId

  // Check if user can edit this profile (own profile OR admin viewing purposecollective)
  const canEditProfile = isOwnProfile || (isAdmin && user.username === 'purposecollective')

  // Load user profile data
  useEffect(() => {
    const loadProfileData = async () => {
      // Check if current user is admin
      if (currentUserId) {
        const adminStatus = await adminService.checkAdminStatus(currentUserId)
        setIsAdmin(adminStatus)
      }

      if (user.id) {
        // Load interests
        try {
          const userInterests = await interestsService.getUserInterests(user.id)
          setInterests(userInterests)
        } catch (error) {
          console.error('Error loading interests:', error)
        }

        // Load bio and location from database
        const { data: profile } = await supabase
          .from('users')
          .select('bio, location')
          .eq('id', user.id)
          .single()

        if (profile) {
          setBio(profile.bio || '')
          setLocation(profile.location || '')
        }

        // Load profile likes count
        try {
          const count = await profilesService.getProfileLikesCount(user.id)
          setLikesCount(count)
        } catch (error) {
          console.error('Error loading profile likes count:', error)
        }

        // Check if current user has liked this profile
        if (currentUserId && currentUserId !== user.id) {
          try {
            const hasLiked = await profilesService.hasUserLikedProfile(user.id, currentUserId)
            setIsLiked(hasLiked)
          } catch (error) {
            console.error('Error checking profile like status:', error)
          }
        }
      }
    }
    loadProfileData()
  }, [user.id, currentUserId])

  const handleProfileLikeToggle = async () => {
    if (!currentUserId || isOwnProfile) return

    try {
      const newLikedState = await profilesService.toggleProfileLike(user.id, currentUserId)
      setIsLiked(newLikedState)
      setLikesCount(prev => newLikedState ? prev + 1 : prev - 1)
    } catch (err: any) {
      console.error('Error toggling profile like:', err)
    }
  }

  return (
    <div className="w-full bg-transparent">
      <div className="max-w-[1000px] mx-auto px-4 pt-8 pb-1 relative">
        {/* Top Right Actions */}
        <div className="absolute top-8 right-4 flex items-center gap-3">
          {/* Love Button - Only show when viewing someone else's profile */}
          {!isOwnProfile && currentUserId && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleProfileLikeToggle}
                className={`p-2 rounded-lg transition-colors ${
                  isLiked
                    ? 'bg-gray-100 text-black hover:bg-gray-200'
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                }`}
                aria-label={isLiked ? 'Unsave profile' : 'Save profile'}
              >
                <Bookmark className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
              </button>
              {likesCount > 0 && (
                <span className="text-sm text-gray-600" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                  {likesCount}
                </span>
              )}
            </div>
          )}

          {/* Edit Profile Button */}
          {canEditProfile && (
            <button
              onClick={() => setShowEditProfileModal(true)}
              className="px-4 py-2 rounded-full bg-white text-black border border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-2"
              style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
            >
              <Pencil className="h-4 w-4" />
              <span className="text-sm font-medium">
                <span className="md:hidden">Edit</span>
                <span className="hidden md:inline">Edit Profile</span>
              </span>
            </button>
          )}
        </div>

        {/* Main Profile Section - Image Left, Info Right */}
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          {/* Left Column - Avatar */}
          <div className="flex-shrink-0">
            <div className="w-[160px] h-[160px] rounded-full overflow-hidden bg-gray-100">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=160&background=e5e7eb&color=6b7280`
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl text-gray-400 font-semibold">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Profile Info */}
          <div className="flex-1 min-w-0">
            {/* Name and Username */}
            <div className="mb-3">
              <h1 className="text-4xl font-light mb-1" style={{ fontFamily: "Canela, Georgia, serif" }}>
                {displayName}
              </h1>
              <p className="text-base text-gray-600" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                @{username}
              </p>
            </div>

            {/* Location */}
            {location && (
              <div className="flex items-center gap-1.5 mb-3">
                <MapPin className="h-4 w-4 text-gray-500" />
                <p className="text-sm text-gray-700" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
                  {location}
                </p>
              </div>
            )}

            {/* Bio */}
            {bio && (
              <p className="text-base text-gray-800 mb-4 leading-relaxed" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
                {bio}
              </p>
            )}

            {/* Interests */}
            {interests.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {interests.map(interest => (
                    <span
                      key={interest.id}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                    >
                      {interest.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs - use wider container when tabAction is present */}
        <div className={`mx-auto border-b border-gray-200 relative ${tabAction ? 'max-w-[1400px]' : ''}`}>
          <div className="flex gap-8 justify-center">
            <button
              onClick={() => onViewModeChange?.('shared')}
              className={`pb-3 uppercase transition-colors ${
                viewMode === 'shared'
                  ? 'text-gray-900 border-b-2 border-gray-900'
                  : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300'
              }`}
              style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
            >
              Shared
            </button>
            <button
              onClick={() => onViewModeChange?.('saved')}
              className={`pb-3 uppercase transition-colors ${
                viewMode === 'saved'
                  ? 'text-gray-900 border-b-2 border-gray-900'
                  : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300'
              }`}
              style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
            >
              Saved
            </button>
            <button
              onClick={() => onViewModeChange?.('issues')}
              className={`pb-3 uppercase transition-colors ${
                viewMode === 'issues'
                  ? 'text-gray-900 border-b-2 border-gray-900'
                  : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300'
              }`}
              style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
            >
              Curated
            </button>
          </div>
          {/* Tab action button - aligned to right */}
          {tabAction && (
            <div className="absolute right-0 top-0 flex items-center h-full pb-2">
              {tabAction}
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        user={user}
        onProfileUpdate={async () => {
          // Refresh user data
          const { data: updatedProfile } = await supabase
            .from('users')
            .select('*')
            .eq('id', user.id)
            .single()

          if (updatedProfile) {
            setDisplayName(`${updatedProfile.first_name || ''} ${updatedProfile.last_name || ''}`.trim() || user.email)
            setAvatarUrl(updatedProfile.avatar_url || '')
            setUsername(updatedProfile.username || user.email?.split('@')[0] || '')
            setBio(updatedProfile.bio || '')
            setLocation(updatedProfile.location || '')
          }

          // Refresh interests
          try {
            const userInterests = await interestsService.getUserInterests(user.id)
            setInterests(userInterests)
          } catch (error) {
            console.error('Error loading interests:', error)
          }

          if (onProfileUpdate) onProfileUpdate()
        }}
      />
    </div>
  )
}